import { useEffect, useState } from 'react';
import { PART_DIMENSION_KEY } from '@featit/shared/view-engine/part-dimension-key';
import type { IPart } from '@/services/PartsApi';
import {
  followGenericViewMetadata,
  forgetGenericViewMetadata,
} from '@/services/genericViewMetadataReads';

/**
 * Whether a work area has parts yet, and its active parts (F-0320, concept §8: "Nothing changes for a
 * work area until its first part is created").
 *
 * ⭐ **Has parts** is the server's one flag, the part dimension's `availability` on the served view
 * metadata (`withPartAvailability`) — the same flag the CLI and MCP read — never a count made here.
 * A deployment whose model has no part dimension at all answers "no parts".
 *
 * Both answers are cached per work area for the page's life, and shared by every caller: the
 * sidebar, the item detail and the create form ask once between them. A change to the tree calls
 * {@link forgetWorkAreaParts} so the next reader asks again, and so does any change to the work
 * area's model, from this tab or another (the doorbell's `parts` frame, I-1533).
 */

type HasPartsStateType = boolean | null;

const hasPartsCache = new Map<string, Promise<boolean>>();
const partsCache = new Map<string, Promise<IPart[]>>();
const listeners = new Set<(workAreaId: string) => void>();

function readHasParts(workAreaId: string): Promise<boolean> {
  let pending = hasPartsCache.get(workAreaId);
  if (!pending) {
    // T-0770 (LC-6b): the sidebar asks this at launch, so the work-item API is loaded for it rather
    // than carried in the entry; the read is still the API's own, shared with every other caller.
    pending = import('@/services/WorkItemApi')
      .then(({ workItemApiFor }) => workItemApiFor(workAreaId).genericViewMetadata(workAreaId))
      .then((metadata) => {
        const part = metadata.dimensions.find((dimension) => dimension.key === PART_DIMENSION_KEY);
        if (!part) return false;
        return (part.availability ?? { available: true }).available;
      })
      .catch((error: unknown) => {
        // A failed read is not an answer: the next reader asks again.
        hasPartsCache.delete(workAreaId);
        throw error;
      });
    hasPartsCache.set(workAreaId, pending);
  }
  return pending;
}

/** The work area's active parts — retired ones are never offered (PT-13). */
export function readWorkAreaParts(workAreaId: string): Promise<IPart[]> {
  let pending = partsCache.get(workAreaId);
  if (!pending) {
    // T-0770: the parts API, and the part queries it reaches, load when a screen asks for the parts.
    pending = import('@/services/PartsApi')
      .then(({ partsApiFor }) => partsApiFor(workAreaId).parts(workAreaId))
      .catch((error: unknown) => {
        partsCache.delete(workAreaId);
        throw error;
      });
    partsCache.set(workAreaId, pending);
  }
  return pending;
}

function dropHeldParts(workAreaId: string): void {
  hasPartsCache.delete(workAreaId);
  partsCache.delete(workAreaId);
}

/**
 * The tree changed: the next reader of this work area asks the server again, and every mounted
 * reader once the burst settles (through the model's one forget path).
 */
export function forgetWorkAreaParts(workAreaId: string): void {
  dropHeldParts(workAreaId);
  forgetGenericViewMetadata(workAreaId);
}

/** How many mounted readers follow each work area, so a change naming no work area reaches them. */
const mountedReaders = new Map<string, number>();

// I-1533: whoever changed the model — a writer here, another person through the doorbell — every
// held answer for that work area is dropped and every mounted reader reads it once more.
followGenericViewMetadata((changed) => {
  const touched =
    changed === 'all'
      ? new Set([...hasPartsCache.keys(), ...partsCache.keys(), ...mountedReaders.keys()])
      : changed;
  for (const workAreaId of touched) {
    dropHeldParts(workAreaId);
    for (const listener of listeners) listener(workAreaId);
  }
});

/** Hears every settled change to a work area's parts while the calling component is mounted. */
function useFollowParts(workAreaId: string | null | undefined, onChange: () => void): void {
  useEffect(() => {
    if (!workAreaId) return;
    mountedReaders.set(workAreaId, (mountedReaders.get(workAreaId) ?? 0) + 1);
    const listener = (changed: string) => {
      if (changed === workAreaId) onChange();
    };
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
      const left = (mountedReaders.get(workAreaId) ?? 1) - 1;
      if (left > 0) mountedReaders.set(workAreaId, left);
      else mountedReaders.delete(workAreaId);
    };
    // `onChange` only bumps the caller's generation, which every render's closure does alike.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workAreaId]);
}

/** `null` while it is being read or could not be read; then whether the work area has parts. */
export function useWorkAreaHasParts(
  workAreaId: string | null | undefined,
  enabled = true,
): HasPartsStateType {
  const [state, setState] = useState<{ id: string | null; hasParts: HasPartsStateType }>({
    id: null,
    hasParts: null,
  });
  const [generation, setGeneration] = useState(0);
  useFollowParts(workAreaId, () => setGeneration((current) => current + 1));

  useEffect(() => {
    if (!workAreaId || !enabled) return;
    let active = true;
    readHasParts(workAreaId).then(
      (hasParts) => {
        if (active) setState({ id: workAreaId, hasParts });
      },
      () => {
        if (active) setState({ id: workAreaId, hasParts: null });
      },
    );
    return () => {
      active = false;
    };
  }, [workAreaId, enabled, generation]);

  return state.id === workAreaId ? state.hasParts : null;
}

export interface IWorkAreaPartsState {
  parts: IPart[] | null;
  error: unknown;
  reload: () => void;
}

/** The work area's active parts, for a picker. `parts` is null until the first read answers. */
export function useWorkAreaParts(workAreaId: string | null | undefined): IWorkAreaPartsState {
  const [state, setState] = useState<{ id: string | null; parts: IPart[] | null; error: unknown }>({
    id: null,
    parts: null,
    error: null,
  });
  const [generation, setGeneration] = useState(0);
  useFollowParts(workAreaId, () => setGeneration((current) => current + 1));

  useEffect(() => {
    if (!workAreaId) return;
    let active = true;
    readWorkAreaParts(workAreaId).then(
      (parts) => {
        if (active) setState({ id: workAreaId, parts, error: null });
      },
      (error: unknown) => {
        if (active) setState({ id: workAreaId, parts: null, error });
      },
    );
    return () => {
      active = false;
    };
  }, [workAreaId, generation]);

  const current = state.id === workAreaId ? state : { parts: null, error: null };
  return {
    parts: current.parts,
    error: current.error,
    reload: () => {
      if (workAreaId) partsCache.delete(workAreaId);
      setGeneration((value) => value + 1);
    },
  };
}
